import React from 'react';

/**
 * Global error boundary. Catches uncaught React render errors that would
 * otherwise blank the entire screen. Shows the actual error message + stack
 * to the user with a "Reload" button so they can recover without restarting
 * the app, and so we can see which line caused the crash in production.
 *
 * Mounted near the root in App.js so every route is protected.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      detailsOpen: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to console so admins/devtools can see the full stack
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Uncaught render error:', error, errorInfo);
    this.setState({ errorInfo });

    // Best-effort: report to backend for support visibility
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token') || '';
      if (API) {
        fetch(`${API}/api/diagnostics/frontend-crash`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            message: String(error?.message || error || 'Unknown error'),
            stack: String(error?.stack || ''),
            componentStack: String(errorInfo?.componentStack || ''),
            url: typeof window !== 'undefined' ? window.location.href : '',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            timestamp: new Date().toISOString(),
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_err) {
      // Swallow — never make the error reporter itself crash
    }
  }

  handleReload = () => {
    try {
      // Soft-reload first — keeps cached assets, just re-runs the app.
      window.location.reload();
    } catch (_e) {
      // ignore
    }
  };

  handleGoHome = () => {
    try {
      window.location.href = '/';
    } catch (_e) {
      // ignore
    }
  };

  toggleDetails = () => {
    this.setState((s) => ({ detailsOpen: !s.detailsOpen }));
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo, detailsOpen } = this.state;
    const message = String(error?.message || error || 'Unknown error');
    const stack = String(error?.stack || '');
    const componentStack = String(errorInfo?.componentStack || '');

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        }}
        data-testid="error-boundary-screen"
      >
        <div
          style={{
            maxWidth: '560px',
            width: '100%',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            padding: '28px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: '#fee2e2',
                color: '#b91c1c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 700,
              }}
              aria-hidden
            >
              !
            </div>
            <h2 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>
              Something went wrong on this page
            </h2>
          </div>

          <p style={{ color: '#475569', fontSize: '14px', marginBottom: '16px', lineHeight: 1.5 }}>
            The action you just performed couldn’t finish because of an unexpected error.
            Your data was not lost — please reload the page and try again. If this keeps
            happening, share the technical details below with the support team.
          </p>

          <div
            style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              padding: '10px 12px',
              borderRadius: '8px',
              fontSize: '13px',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              wordBreak: 'break-word',
              marginBottom: '16px',
            }}
            data-testid="error-boundary-message"
          >
            {message}
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleReload}
              style={{
                background: '#0ea5e9',
                color: '#fff',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                background: '#fff',
                color: '#0f172a',
                border: '1px solid #cbd5e1',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
              data-testid="error-boundary-home"
            >
              Go to home
            </button>
            <button
              onClick={this.toggleDetails}
              style={{
                background: 'transparent',
                color: '#475569',
                border: 'none',
                padding: '10px 4px',
                fontSize: '13px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              data-testid="error-boundary-toggle-details"
            >
              {detailsOpen ? 'Hide details' : 'Show technical details'}
            </button>
          </div>

          {detailsOpen && (
            <pre
              style={{
                marginTop: '16px',
                padding: '12px',
                background: '#0f172a',
                color: '#e2e8f0',
                borderRadius: '8px',
                fontSize: '11px',
                lineHeight: 1.4,
                maxHeight: '260px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
              data-testid="error-boundary-details"
            >
              {`URL: ${typeof window !== 'undefined' ? window.location.href : ''}\n\nError:\n${message}\n\nStack:\n${stack}\n\nComponent stack:${componentStack}`}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
